import { Dialog, DialogContent, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useContext } from 'react';

import { UserContext } from '~/auth/userContext';
import { DownloadStampedInvoiceDropdownMenu } from '~/components/invoices/stampedInvoice/Dropdown';
import { type Invoice } from '~/data/schemas/invoice';
import { InvoiceActionType, invoiceKindToLabelMap } from '~/data/schemas/invoice';
import { formatCurrency } from '~/utils/format';

interface ConfirmationDialogProps {
  actionType: InvoiceActionType;
  invoice: Invoice;
  onClose: () => void;
}
const getActionVerb = (actionType: InvoiceActionType) => {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (actionType) {
    case InvoiceActionType.PARTIAL:
      return 'Partially Approved';
    case InvoiceActionType.REJECT:
      return 'Rejected';
    case InvoiceActionType.APPROVE:
      return 'Approved';
    default:
      throw new Error('Unknown action type');
  }
};

const getActionColor = (actionType: InvoiceActionType) => {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (actionType) {
    case InvoiceActionType.PARTIAL:
      return 'warning.main';
    case InvoiceActionType.REJECT:
      return 'error.main';
    case InvoiceActionType.APPROVE:
      return 'success.main';
    default:
      throw new Error('Unknown action type');
  }
};

// confirmation dialog for each of the invoice actions
const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  actionType,
  invoice,
  onClose,
}) => {
  const { currencyCode } = useContext(UserContext);
  const invoiceOrProposal = invoiceKindToLabelMap.get(invoice.kind)!;
  const hasSaving = (invoice.savings ?? 0) > 0;
  const formattedSavings = hasSaving
    ? formatCurrency({ value: invoice.savings ?? null, currencyCode })
    : null;
  const title = hasSaving
    ? `Congratulations, you saved your client ${formattedSavings}!`
    : 'One less to go!';
  const content = `${invoiceOrProposal} #${invoice.number} has been `;

  return (
    <Dialog open onClose={onClose} maxWidth="md">
      <DialogContent>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          spacing={1}
        >
          <Grid>
            <img src="/images/high-five.svg" alt="Congratulations" />
          </Grid>
          <Grid>
            <Typography variant="h4" color="text.primary">
              {title}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="h5" component="span" color="text.primary">
              {content}
            </Typography>
            &nbsp;
            <Typography
              variant="h5"
              component="span"
              color={getActionColor(actionType)}
              sx={{ fontWeight: 'bold' }}
            >
              {getActionVerb(actionType)}
            </Typography>
          </Grid>
          <Grid mt={3} mb={2}>
            <DownloadStampedInvoiceDropdownMenu invoice={invoice} />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmationDialog;
